import DashboardLayout from "../../../components/layout/DashboardLayout";

import { useCustomerViewModel } from "../../../viewmodels/operation/customer/useCustomerViewModel";

import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";

const CustomersPage = () => {
  const navigate = useNavigate();

  const {
    customers,
    loading,

    page,
    setPage,

    search,
    setSearch,

    deleteCustomer,
  } = useCustomerViewModel();

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Customer?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      await deleteCustomer(id);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Customer deleted successfully.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: "Unable to delete customer.",
      });
    }
  };

  return (
    <DashboardLayout>
      <div className="p-3 md:p-6 w-full">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Customers</h1>

            <p className="text-gray-500 mt-1 text-sm">
              Manage customer records
            </p>
          </div>

          <button
            onClick={() => navigate("/customers/create")}
            className="bg-primary text-white px-5 py-3 rounded-xl w-full md:w-auto"
          >
            + Create Customer
          </button>
        </div>

        {/* SEARCH */}
        <div className="bg-white rounded-xl shadow border border-slate-300 p-4 mb-5">
          <label className="block text-sm font-medium mb-2">Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search customer..."
            className="w-full border border-slate-300 rounded-xl px-4 py-3"
          />
        </div>

        {/* TABLE */}
        <div className="bg-white rounded-xl shadow border border-slate-300 overflow-hidden">
          {/* MOBILE SCROLL ONLY */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm text-left table-fixed">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 border border-slate-300 w-[20%]">Name</th>

                  <th className="p-3 border border-slate-300 w-[25%]">Email</th>

                  <th className="p-3 border border-slate-300 w-[35%]">
                    Phone Numbers
                  </th>

                  <th className="p-3 border border-slate-300 w-[20%] text-center">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center p-6 border border-slate-300"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : customers.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="text-center p-6 border border-slate-300 text-gray-500"
                    >
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customers.map((x: any) => (
                    <tr key={x.id} className="hover:bg-gray-50 align-top">
                      {/* NAME */}
                      <td className="p-3 border border-slate-300 break-words font-medium">
                        {x.name}
                      </td>

                      {/* EMAIL */}
                      <td className="p-3 border border-slate-300 break-words">
                        {x.email || "-"}
                      </td>

                      {/* PHONE NUMBERS */}
                      <td className="p-3 border border-slate-300">
                        <div className="space-y-2">
                          {x.customerPhoneNumbers?.length > 0 ? (
                            x.customerPhoneNumbers.map((p: any, i: number) => (
                              <div
                                key={i}
                                className="border rounded-lg p-2 bg-slate-50"
                              >
                                <div className="font-semibold text-sm break-words">
                                  {p.name}
                                </div>

                                <div className="text-gray-700 text-sm break-words mt-1">
                                  {p.phoneNumber}
                                </div>
                              </div>
                            ))
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>

                      {/* ACTIONS */}
                      <td className="p-3 border border-slate-300">
                        <div className="flex flex-col md:flex-row gap-2 justify-center">
                          <button
                            onClick={() => navigate(`/customers/edit/${x.id}`)}
                            className="bg-yellow-500 text-white px-3 py-2 rounded-lg text-sm"
                          >
                            Edit
                          </button>

                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            disabled={page === 1}
            onClick={() => setPage((p: number) => Math.max(1, p - 1))}
            className="px-4 py-2 border rounded-lg disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={() => setPage(page + 1)}
            className="px-4 py-2 border rounded-lg"
          >
            Next
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CustomersPage;
